import React from "react";
import styles from "./Configuration.module.scss";
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Tooltip,
  Typography,
} from "antd";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { sanitizeDataTypeClientSide } from "../../../../../../utils/utils";
import { colors } from "../../../../../../constants/colors";
import { InfoButtonIcon } from "../../../../../../assets/svg";
import { useDispatch } from "react-redux";
import { updateRefundAction } from "../../../../../../redux/services/configuration/configurationServices";
import { allowCharactersOnlyForDecimalNumbers } from "../../../../../../utils/appKeyStrokeLimitingUtils";
import { routes } from "../../../../../../constants";
import { baseRouteMakerForSuperAdmin } from "../../../../../../constants/routeConsts";
import { useNavigate } from "react-router-dom";

const Configuration = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch dependencies */
  const dispatch = useDispatch();
  /* dispatch dependencies */

  const navigate = useNavigate();

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const type = Form.useWatch("type", { form, preserve: true });
  const time_limit_hours = 48;

  const initialValues = {
    type: null,
    organization_fine_percentage: "",
    after_time_limit_refund_percentage: "",
    before_time_limit_refund_percentage: ""
  };
  /* form dependencies */

  /* on Category changed */
  const onCategoryChanged = () => {
    resetDefaultFields()
  };
  /* on Category changed */

  /* reset default form fields */
  const resetDefaultFields = () => {
    form.setFieldsValue({
      organization_fine_percentage: initialValues.organization_fine_percentage,
      after_time_limit_refund_percentage: initialValues.after_time_limit_refund_percentage,
      before_time_limit_refund_percentage: initialValues.before_time_limit_refund_percentage
    });
  }
  /* reset default form fields */

  const handleViewList = () => {
    navigate(baseRouteMakerForSuperAdmin(routes.SALES_PAGE_CONFIGURATION_LIST))
  }

  /* on submit */
  const onSubmit = (values) => {
    const dataToBeSent = {}
    switch (type) {
      case "customer":
        dataToBeSent.user_type = values?.type;
        dataToBeSent.time_limit_hours = time_limit_hours;
        dataToBeSent.after_time_limit_refund_percentage = Number(values?.after_time_limit_refund_percentage);
        dataToBeSent.before_time_limit_refund_percentage = Number(values?.before_time_limit_refund_percentage);
        break;
        
      case "service_provider":
        dataToBeSent.user_type = values?.type;
        dataToBeSent.organization_fine_percentage = Number(values?.organization_fine_percentage);
      break;
    
      default:
      break;
    }

    dispatch(updateRefundAction(dataToBeSent))
    resetDefaultFields()
  };
  /* on submit */

  return (
    <div className={styles.configurationContainer}>
      <Flex vertical gap="2rem">
        <Flex justify="space-between">
        <Typography.Title level={2}>{t("appointmentCancellation")}</Typography.Title>
          <Button
              type="primary"
              loading={loading}
              disabled={loading}
              style={{ marginTop: "10px" }}
              onClick={(e) => handleViewList(e)}
            >
              {t("CommonUtils.logs")}
            </Button>
        </Flex>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          style={{ maxWidth: "400px" }}
          onFinish={onSubmit}
          initialValues={initialValues}
        >
          <Form.Item
            name="type"
            label={
              <RequiredLabel
                text={t("Modules.Configuration.userType")}
                required={true}
              />
            }
            rules={[
              {
                required: true,
                message: t("Modules.Configuration.pleaseSelectUserType"),
              },
            ]}
          >
            <Select
              loading={selectListLoader}
              options={[
                { value: "service_provider", label: "Service Provider" },
                { value: "customer", label: "User" },
              ]}
              placeholder={t("Modules.Configuration.selectUserType")}
              onChange={onCategoryChanged}
            />
          </Form.Item>
          {type === "customer" && (
            <>
              {/* <Form.Item
                name="selection"
                label={
                  <RequiredLabel
                    text={t("Modules.Configuration.cancellationTime")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t(
                      "Modules.Configuration.pleaseSelectCancellationTime"
                    ),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="before48hours">
                    {t("Modules.Configuration.before48hours")}
                  </Radio>
                  <Radio value="after48hours">
                    {t("Modules.Configuration.after48hours")}
                  </Radio>
                </Radio.Group>
              </Form.Item> */}

              <Form.Item
                name="before_time_limit_refund_percentage"
                label={
                  <RequiredLabel
                    text={t("Modules.Configuration.beforeTimeLimitRefundPercentage")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Configuration.pleaseEnterBeforeTimeLimitRefund"),
                  },
                  {
                    validator:(_,value)=>{
                        if(value && value > 100 ){
                          return Promise.reject(`${t("Modules.Configuration.refund")} ${t("CommonUtils.shouldNotBeGreaterThan100")}`)
                        }
                        else if(value && value < 0){
                          return Promise.reject(`${t("Modules.Configuration.refund")} ${t("CommonUtils.shouldBeAPositiveInteger")}`)
                        }
                      return Promise.resolve();
                    }
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder={t("Modules.Configuration.enterRefund")}
                  onKeyDown={(e)=>allowCharactersOnlyForDecimalNumbers(e)}
                />
              </Form.Item>

              <Form.Item
                name="after_time_limit_refund_percentage"
                label={
                  <RequiredLabel
                    text={t("Modules.Configuration.afterTimeLimitRefundPercentage")}
                    required={true}
                  />
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Configuration.pleaseEnterAfterTimeLimitRefund"),
                  },
                  {
                    validator:(_,value)=>{
                        if(value && value > 100 ){
                          return Promise.reject(`${t("Modules.Configuration.refund")} ${t("CommonUtils.shouldNotBeGreaterThan100")}`)
                        }
                        else if(value && value < 0){
                          return Promise.reject(`${t("Modules.Configuration.refund")} ${t("CommonUtils.shouldBeAPositiveInteger")}`)
                        }
                      return Promise.resolve();
                    }
                  },
                ]}
              >
                <Input
                  type="number"
                  placeholder={t("Modules.Configuration.enterRefund")}
                  onKeyDown={(e)=>allowCharactersOnlyForDecimalNumbers(e)}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
                style={{ marginTop: "10px" }}
              >
                {t("CommonUtils.update")}
              </Button>
            </>
          )}
          {type === "service_provider" && (
            <>
              <Form.Item
                name="organization_fine_percentage"
                label={
                  <Flex gap="1rem" align="center" justify="center">
                    <RequiredLabel text={t("Modules.Configuration.doctorNoShow")} required={true}/>
                    <Tooltip
                      placement="rightTop"
                      title={t("Modules.Configuration.deductionPercentTooltipInfo")}
                      color={colors.DarkMint}
                    >
                      <Button type="text" shape="circle" size="small">
                        <Flex align="center" justify="center">
                          <InfoButtonIcon />
                        </Flex>
                      </Button>
                    </Tooltip>
                  </Flex>
                }
                rules={[
                  {
                    required: true,
                    message: t("Modules.Configuration.pleaseEnterDeductionPercent")
                  },
                  {
                    validator:(_,value)=>{
                        if(value && value > 100 ){
                          return Promise.reject(`${t("Modules.Configuration.doctorNoShow")} ${t("CommonUtils.shouldNotBeGreaterThan100")}`)
                        }
                        else if(value && value < 1){
                          return Promise.reject(`${t("Modules.Configuration.doctorNoShow")} ${t("CommonUtils.shouldBeGreaterThan0")}`)
                        }
                      return Promise.resolve();
                    }
                  }
                ]}>
                <Input
                  type="number"
                  placeholder={t("Modules.Configuration.enterDeductionPercent")}
                  onKeyDown={(e)=>allowCharactersOnlyForDecimalNumbers(e)}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={loading}
                style={{ marginTop: "10px" }}
              >
                {t("CommonUtils.update")}
              </Button>
            </>
          )}
        </Form>
      </Flex>
    </div>
  );
};

export default Configuration;
